import type {
  SessionCloneView,
  SessionNotePreview,
  SessionResumeView,
  SubmittedDatasetRef,
  TrainingSessionListView,
  TrainingSessionView,
} from "@/api";
import { formatDateTime, parseUtcMs } from "@/utils/datetime";
import { registryDatasetDisplayLabel, sessionSimpleStatus } from "@/modules/training/public";

// 세션 목록 행의 표시 파생값. 이름은 null 일 수 있어 폴백(id 단축)을 둔다. 컴포넌트는 이 순수
// 함수들만 호출해 얇게 유지한다.

function shortId(id: string): string {
  return `${id.slice(0, 8)}…`;
}

// 표시명. 없으면 id 단축 값을 쓴다.
export function sessionDisplayName(
  session: Pick<TrainingSessionListView, "display_name" | "id">,
): string {
  return session.display_name ?? shortId(session.id);
}

// experiment 표시. 이름이 없으면 id 단축 값, 둘 다 없으면 "—" 다.
export function sessionExperimentLabel(
  session: Pick<TrainingSessionListView, "experiment_name" | "experiment_id">,
): string {
  if (session.experiment_name !== null) {
    return session.experiment_name;
  }
  if (session.experiment_id !== null) {
    return shortId(session.experiment_id);
  }
  return "—";
}

export function sessionWorkspaceLabel(
  session: Pick<TrainingSessionListView, "workspace_name">,
): string {
  return session.workspace_name ?? "—";
}

// 생성일 셀. 보는 사람의 타임존 기준 `YYYY-MM-DD HH:mm` 이다.
export function formatSessionCreatedAt(iso: string | null): string {
  return formatDateTime(parseUtcMs(iso));
}

// kind 가 없으면 상류 step type 이 전달된다. 그래서 두 어휘를 같은 단계로 읽는다.
const TRAINING_PROGRESS_STAGE_LABELS: Record<string, string> = {
  train: "Training",
  training: "Training",
  train_only: "Training",
  train_eval_quick: "Training",
  preparing: "준비",
  validating: "검증",
  evaluating: "Evaluation",
  evaluation: "Evaluation",
  eval: "Evaluation",
  eval_only: "Evaluation",
  preprocess: "전처리",
  dataset_preprocess: "전처리",
  dataset_preprocessing: "전처리",
  probe: "점검",
  dataset_probe: "점검",
  publishing: "게시",
};

export function trainingProgressStageLabel(stage: string): string {
  const key = stage.trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(TRAINING_PROGRESS_STAGE_LABELS, key)
    ? TRAINING_PROGRESS_STAGE_LABELS[key]
    : "진행 중";
}

// 마지막 단계는 종료 후에도 남는다. 실행 중에만 실제 단계 진행률을 상태에 합친다.
export function formatSessionStatus(
  session: Pick<TrainingSessionListView, "status" | "progress">,
): string {
  const { status, progress } = session;
  if (status.toLowerCase() !== "running" || progress === null) {
    return sessionSimpleStatus(status).label;
  }
  const stageLabel =
    progress.stage === null ? "진행 중" : trainingProgressStageLabel(progress.stage);
  if (stageLabel === "Evaluation") {
    return "Evaluating";
  }
  const stage = stageLabel === "진행 중" ? "진행" : stageLabel;
  if (progress.ratio === null || !Number.isFinite(progress.ratio)) {
    return `${stage} 중`;
  }
  const percent = Math.round(Math.min(1, Math.max(0, progress.ratio)) * 100);
  return `${stage} ${percent}%`;
}

// 입력 목록에서 목록 한 칸이 대표할 하나를 고른다. 학습 타깃이 있으면 타깃, 없으면 첫 train 입력,
// 그것도 없으면 첫 항목이다. 타깃은 샘플링 기준점이라 그 세션을 가장 잘 설명한다.
//
// 서버 목록 행(`dataset`)은 같은 규칙을 저널에 적용해 보낸다. 그래서 이 함수는 전체 입력을 가진
// 제출 응답을 행 형태로 변환할 때만 쓴다(`submitViewToListView`).
export function representativeSessionDataset(
  datasets: readonly SubmittedDatasetRef[],
): SubmittedDatasetRef | null {
  return leadingSessionDatasets(datasets)[0] ?? null;
}

// 서버 목록 행이 싣는 상위 입력 수(`leading_datasets`).
const LEADING_DATASET_LIMIT = 3;

// 입력 목록을 대표 순서(학습 타깃 → train → 제출 순)로 세운 상위 몇 건. 서버 목록 행의
// `leading_datasets` 와 같은 규칙이다. 첫 항목이 대표다.
export function leadingSessionDatasets(
  datasets: readonly SubmittedDatasetRef[],
): readonly SubmittedDatasetRef[] {
  const rank = (entry: SubmittedDatasetRef): number =>
    entry.is_target ? 0 : entry.role === "train" ? 1 : 2;
  // sort 는 안정 정렬이다. 같은 순위 안에서는 제출 순서가 유지된다.
  return [...datasets].sort((a, b) => rank(a) - rank(b)).slice(0, LEADING_DATASET_LIMIT);
}

// registry 원본이 지워진 입력의 placeholder. 제출 저널이 남아 있어 무엇으로 실행했는지는 알지만
// 이름을 복원할 수 없다.
export const DELETED_DATASET_LABEL = "삭제된 Dataset";

/**
 * 입력 하나의 표시 이름. 네 가지 경우로 나뉜다:
 * - registry 참조가 있으면 그 표시명
 * - 참조는 없고 저널의 uuid 가 있으면 그 Dataset 이 지워진 것
 * - 상류 이름이 있으면 그 이름(training 이 직접 만든 데이터셋의 실제 이름)
 * - 아무것도 없으면 training-infra 전용 표기
 *
 * registry 내부 등록 이름(`reg-ds-<uuid>`)은 서버가 이미 지워 보낸다. 그래서 여기 오는
 * `upstream_dataset_label` 은 사람이 읽을 뜻이 있는 이름뿐이다.
 */
export function submittedDatasetLabel(entry: SubmittedDatasetRef): string {
  if (entry.registry_ref == null && entry.registry_dataset_uuid !== null) {
    return DELETED_DATASET_LABEL;
  }
  return registryDatasetDisplayLabel(entry.registry_ref, entry.upstream_dataset_label);
}

// 입력 하나의 사용 방식. 제출 위저드의 입력 요약과 같은 어휘다. 예) "train · weight 4 · target"
// 지정되었을 때만 비율과 학습 타깃을 적는다. 지정하지 않은 비율은 균등이라 적지 않는다.
// weight 는 임의의 양수이고 화면은 입력 전부를 한 번에 가지지 않는다. 그래서 퍼센트로 바꾸지 않는다.
export function submittedDatasetUsage(entry: SubmittedDatasetRef): string {
  const parts = [entry.role];
  if (entry.weight !== null && entry.weight !== undefined) {
    parts.push(`weight ${entry.weight}`);
  }
  if (entry.is_target) {
    parts.push("target");
  }
  return parts.join(" · ");
}

// 이름만 말줄임할 수 있도록 대표 이름과 추가 건수를 분리한다. 나머지 입력은 상세에서 본다.
export function sessionDatasetLabel(session: Pick<TrainingSessionListView, "dataset">): string {
  if (session.dataset === null) {
    return "—";
  }
  return submittedDatasetLabel(session.dataset);
}

/**
 * 대표 외에 몇 개가 더 있는지. 표 셀이 이름과 **따로** 표시하는 수다.
 *
 * 한 문자열이면 잘림이 수를 먼저 잘라서 3개를 섞은 세션이 1개짜리와 같아 보인다.
 */
export function sessionDatasetExtraCount(
  session: Pick<TrainingSessionListView, "dataset_count">,
): number {
  return session.dataset_count > 1 ? session.dataset_count - 1 : 0;
}

// 노트 유무. 공백뿐인 본문은 없는 것으로 본다. 노트 셀이 표식을 표시할지 정하는 단일 기준이다.
export function sessionHasNote(note: SessionNotePreview | null): boolean {
  return note !== null && note.note.trim() !== "";
}

// 노트 본문 한 줄. 여러 줄을 공백 한 칸으로 합친다(전체 보기는 셀의 hover 툴팁이 맡는다).
// 노트가 없거나 공백뿐이면 "—" 다.
export function formatSessionNote(note: SessionNotePreview | null): string {
  if (note === null || !sessionHasNote(note)) {
    return "—";
  }
  return note.note.replace(/\s+/g, " ").trim();
}

// 이어서 학습·복제로 만드는 새 세션의 기본 이름. 부모 이름에서 파생한다.
//
// FE 는 본문에 이름을 포함하지 않고(구 상류가 422 를 반환하는 문제) 만든 뒤 rename 으로 적용한다.
// 그래서 제출 전에 이름을 묻고 연구자 관례로 기본값을 채운다(dev 실데이터의 `-x`→`-x2`→`-x3`,
// `-r2`/`-r3`). 끝의 정수를 올리고, 없으면 `2` 를 붙인다. 상류 자동 파생(clone/resume `{name?}`
// 생략 경로)도 같은 정수 증가 규칙이라 관례가 하나로 유지된다. 상류가 이름 지정 경로를 열면 이
// 함수는 rename 왕복과 함께 제거한다.
export function derivedSessionDefaultName(parentName: string, maxLength: number): string {
  const trimmed = parentName.trim();
  const trailingNumber = /^(.*?)(\d+)$/.exec(trimmed);
  const base = trailingNumber === null ? trimmed : trailingNumber[1];
  const suffix = trailingNumber === null ? "2" : String(Number(trailingNumber[2]) + 1);
  // 상한을 넘으면 접미사가 남도록 앞을 자른다. 잘려도 체인 위치를 읽을 수 있어야 한다.
  return `${base.slice(0, Math.max(0, maxLength - suffix.length))}${suffix}`;
}

// 이어서 학습·복제 응답을 상세 Window 가 받는 세션 행 형태로 변환한다. 두 응답 모두
// workspace·experiment·생성 시각·입력 목록이 없어서 비워 둔다. 상세 Window 가 rich 를 직접 받는다.
//
// displayName 은 rename 을 반영한 값이다. rename 이 실패하면 상류가 만든 이름(`sess-…`)이 그대로
// 온다. 화면이 실제 저장된 이름과 다른 이름을 표시하면 안 되므로 호출부가 결과를 전달한다.
export function createdSessionToListView(
  view: SessionResumeView | SessionCloneView,
  displayName: string | null,
): TrainingSessionListView {
  return {
    id: view.id,
    status: view.status,
    display_name: displayName ?? view.display_name,
    experiment_id: null,
    experiment_name: null,
    workspace_id: null,
    workspace_name: null,
    created_at: null,
    started_at: null,
    finished_at: null,
    progress: null,
    schedule_origin: null,
    note: null,
    registry_user: null,
    dataset: null,
    dataset_count: 0,
    leading_datasets: [],
    init_strategy: null,
  };
}

// 제출 응답을 상세 Window 가 받는 세션 행 형태로 변환한다. 제출 응답에는 workspace 이름·진행률·
// 노트·선점자가 없어서 null 로 둔다(워크스페이스는 "—"로 표시한다). 상세 Window 가 rich 를 직접
// 받는다(제출 직후 창은 owned 플래그로 소유를 확정한다).
export function submitViewToListView(view: TrainingSessionView): TrainingSessionListView {
  return {
    id: view.training_session_id,
    status: view.status ?? "",
    display_name: view.display_name,
    experiment_id: null,
    experiment_name: view.experiment_display_name,
    workspace_id: view.workspace_id,
    workspace_name: null,
    created_at: view.created_at,
    started_at: null,
    finished_at: null,
    progress: null,
    schedule_origin: null,
    note: null,
    registry_user: null,
    // 제출 응답이 이미 입력 목록을 가지고 있다. 그래서 서버 행과 같은 규칙으로 대표와 개수를 고른다.
    // 그래서 방금 만든 세션의 데이터셋 칸만 목록에서 비어 보이지 않는다.
    dataset: representativeSessionDataset(view.datasets),
    dataset_count: view.datasets.length,
    leading_datasets: leadingSessionDatasets(view.datasets),
    // 제출 응답에는 시작점이 없다. 목록을 다시 받으면 채워진다.
    init_strategy: null,
  };
}
